/**
 * PDF/Word Improvement page component for ClarCV application.
 * 
 * Allows users to upload their existing CV in PDF or Word (.docx) format and get
 * AI-powered improvements with better formatting, content optimization,
 * and ATS compatibility.
 * 
 * @component
 * @returns {JSX.Element} CV Improvement page component
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import TemplateGallery from '../components/TemplateGallery';
import AnalyzingProgress from '../components/AnalyzingProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { useRequireAuth } from '../contexts/AuthGateContext';
import { buildCvDownloadFilename, filenameFromAnalysisData } from '../utils/downloadFilename';
import { usePlanLimitPrompt } from '../hooks/usePlanLimitPrompt';

/**
 * CV Improvement component that handles PDF/Word upload, analysis, and improvement.
 * 
 * @returns {JSX.Element} The CV improvement interface with upload and analysis
 */
const PDFImprovement = () => {
  const { t } = useLanguage();
  const requireAuth = useRequireAuth();
  const { handlePlanLimitError, prompt: upgradePrompt } = usePlanLimitPrompt();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [improvedPDF, setImprovedPDF] = useState(null);
  const [step, setStep] = useState(1); // 1: Upload, 2: Analysis, 3: Results
  const [pageLimit, setPageLimit] = useState('2_pages'); // Options: '1_page', '2_pages', 'auto'
  const [layoutStyle, setLayoutStyle] = useState('standard'); // Options: 'compact', 'standard', 'detailed'
  // NOTE: Original style preservation REMOVED - Always use professional predefined styles
  const [changeSummary, setChangeSummary] = useState(null); // Summary of changes made in overlay mode
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  /**
   * Handle file selection
   */
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Selecciona un archivo PDF o Word (.docx)');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('El archivo debe pesar menos de 10 MB');
        return;
      }
      setFile(selectedFile);
      setChangeSummary(null); // Clear change summary when new file is uploaded
      const fileType = selectedFile.type === 'application/pdf' ? 'PDF' : 'Word';
      toast.success(`Archivo ${fileType} seleccionado correctamente`);
    }
  };

  /**
   * Handle file upload and analysis
   */
  const handleUploadAndAnalyze = async () => {
    if (!file) {
      toast.error('Selecciona primero un archivo PDF o Word');
      return;
    }
    if (!aiConsent) {
      toast.error('Acepta el consentimiento de uso de IA para continuar.');
      return;
    }
    if (!(await requireAuth())) {
      return;
    }

    setUploading(true);
    setChangeSummary(null); // Clear change summary on new analysis
    setStep(2);

    try {
      // REAL ANALYSIS - Upload file to backend for analysis
      const formData = new FormData();
      formData.append('pdf_file', file);
      formData.append('analysis_type', 'improvement');
      formData.append('ai_consent', 'true');

      const analyzingToastId = toast.info('Analizando el contenido del PDF con IA...', {
        autoClose: false,
      });
      
      // Call real API endpoint for PDF analysis
      const response = await axios.post('/api/cvs/analyze-pdf/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('DEBUG: Analysis response:', response.data);
      console.log('DEBUG: Improvements count:', response.data.improvements?.length || 0);
      setAnalysis(response.data);
      setStep(3);
      
      // Replace the "analyzing" toast with a single success message
      if (response.data.from_cache) {
        toast.update(analyzingToastId, {
          render: 'Análisis listo (caché) — sin nueva llamada a la API',
          type: 'success',
          autoClose: 3500,
          isLoading: false,
        });
      } else if (response.data.ai_mode === 'openai') {
        toast.update(analyzingToastId, {
          render: 'Análisis completado',
          type: 'success',
          autoClose: 3000,
          isLoading: false,
        });
      } else {
        toast.update(analyzingToastId, {
          render: 'Análisis completado',
          type: 'success',
          autoClose: 3000,
          isLoading: false,
        });
      }
      
    } catch (error) {
      console.error('Error analyzing PDF:', error);
      if (await handlePlanLimitError(error)) {
        setStep(1);
        return;
      }
      
      // Show detailed error message
      let errorMessage = 'Error al analizar el PDF. Inténtalo de nuevo.';
      
      if (error.response) {
        const errorData = error.response.data;

        if (errorData.error_code === 'pdf_no_text') {
          toast.error(t('flow.errPdfNoText'), { autoClose: 8000 });
          setStep(1);
          return;
        }
        
        // Check for OpenAI specific errors
        if (errorData.error_type === 'openai_error') {
          errorMessage = errorData.error;
          
          // Show specific guidance for quota errors
          if (errorMessage.includes('quota exceeded') || errorMessage.includes('insufficient_quota')) {
            toast.error(
              <div>
                <strong>⚠️ Cuota de la API de OpenAI agotada</strong>
                <p style={{ marginTop: '8px', fontSize: '0.9em' }}>
                  {errorMessage}
                </p>
                <p style={{ marginTop: '8px', fontSize: '0.85em', color: '#f59e0b' }}>
                  Configura tu clave de API de OpenAI con créditos disponibles.
                </p>
              </div>,
              { autoClose: 8000 }
            );
          } else if (errorMessage.includes('API key')) {
            toast.error(
              <div>
                <strong>⚠️ Error de configuración de la API de OpenAI</strong>
                <p style={{ marginTop: '8px', fontSize: '0.9em' }}>
                  {errorMessage}
                </p>
              </div>,
              { autoClose: 6000 }
            );
          } else {
            toast.error(errorMessage, { autoClose: 6000 });
          }
        } else {
          toast.error(errorData.error || errorMessage);
        }
      } else {
        toast.error(errorMessage);
      }
      
      setStep(1);
    } finally {
      setUploading(false);
    }
  };




  /**
   * Generate improved CV
   */
  const handleGenerateImprovedPDF = async () => {
    if (!analysis) return;
    if (!(await requireAuth())) {
      return;
    }

    setAnalyzing(true);
    
    // Clear previous PDF to show we're regenerating
    const isRegenerating = improvedPDF !== null;
    if (isRegenerating) {
      setImprovedPDF(null);
      toast.info('🔄 Regenerando el CV con el nuevo diseño...', { autoClose: 2000 });
    } else {
      toast.info('✨ Generando CV mejorado...', { autoClose: 2000 });
    }

    try {
      // Detect file format
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      // REAL PDF GENERATION - Call backend API with layout options
      const formData = new FormData();
      formData.append('analysis_data', JSON.stringify(analysis));
      formData.append('output_format', fileExtension);
      formData.append('page_limit', pageLimit);
      formData.append('layout_style', layoutStyle);
      
      // CRITICAL: For Word documents, ALWAYS preserve the original layout/format
      // This ensures only text changes, not the visual appearance
      if (fileExtension === 'docx') {
        formData.append('preserve_layout', 'true');
        console.log('DEBUG: Word document detected - preserve_layout enabled');
        toast.info('🎨 Conservando el formato Word original — solo se mejora el texto', { autoClose: 3000 });
      } else {
        // For PDFs, show layout selection
        const layoutText = pageLimit === '1_page' ? '1 página compacta' : pageLimit === '2_pages' ? '2 páginas estándar' : 'multpágina detallada';
        toast.info(`📄 Aplicando diseño de ${layoutText}...`, { autoClose: 3000 });
      }
      
      // NOTE: Original style preservation REMOVED for PDFs - Always use professional predefined styles
      
      const response = await axios.post('/api/cvs/generate-improved-cv/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob'
      });

      // Check if there's a change summary in the response headers (overlay mode)
      console.log('DEBUG: All response headers:', response.headers);
      console.log('DEBUG: Looking for x-change-summary header...');
      
      const changeSummaryHeader = response.headers['x-change-summary'];
      console.log('DEBUG: x-change-summary value:', changeSummaryHeader);
      
      if (changeSummaryHeader) {
        try {
          const summary = JSON.parse(changeSummaryHeader);
          setChangeSummary(summary);
          console.log('✅ Change summary received and parsed:', summary);
        } catch (e) {
          console.error('❌ Error parsing change summary:', e);
          setChangeSummary(null);
        }
      } else {
        console.log('⚠️ No change summary header found');
        setChangeSummary(null);
      }

      // Create blob from response
      const blob = new Blob([response.data], { 
        type: fileExtension === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const improvedCV = {
        blob: blob,
        filename: analysis
          ? filenameFromAnalysisData(analysis, fileExtension)
          : buildCvDownloadFilename({
              name: file?.name?.replace(/\.(pdf|docx)$/i, ''),
              extension: fileExtension,
            }),
        originalFormat: fileExtension,
        size: `${(blob.size / 1024 / 1024).toFixed(1)} MB`,
        improvements_applied: analysis.improvements?.length || 0
      };

      setImprovedPDF(improvedCV);
      
      // Success message with layout info
      const layoutDesc = pageLimit === '1_page' ? '1 página compacta' : pageLimit === '2_pages' ? '2 páginas estándar' : 'detallado';
      const styleDesc = layoutStyle === 'compact' ? 'Compacto' : layoutStyle === 'detailed' ? 'Detallado' : 'Estándar';
      
      if (isRegenerating) {
        toast.success(`✅ CV regenerado con diseño ${layoutDesc}`, { 
          autoClose: 4000,
          position: "top-center"
        });
      } else {
        toast.success(`✅ CV mejorado generado correctamente (${layoutDesc} - ${styleDesc})`, { 
          autoClose: 4000,
          position: "top-center"
        });
      }
      
      // Scroll to results
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
      
    } catch (error) {
      console.error('Error generating improved CV:', error);
      if (await handlePlanLimitError(error)) {
        return;
      }
      toast.error('❌ Error al generar el CV mejorado. Inténtalo de nuevo.', {
        autoClose: 5000
      });
    } finally {
      setAnalyzing(false);
    }
  };


  /**
   * Download change report as TXT
   */
  const handleDownloadChangeReport = () => {
    if (!changeSummary) {
      toast.error('No hay resumen de cambios disponible');
      return;
    }

    try {
      // Generate report content
      const timestamp = new Date().toLocaleString('es-ES');
      let reportContent = `===========================================\n`;
      reportContent += `REPORTE DE CAMBIOS - CV MEJORADO\n`;
      reportContent += `===========================================\n`;
      reportContent += `Fecha: ${timestamp}\n`;
      reportContent += `Modo: Overlay (Preservación exacta del formato)\n\n`;
      
      reportContent += `ESTADÍSTICAS:\n`;
      reportContent += `===========================================\n`;
      reportContent += `- Bloques de texto analizados: ${changeSummary.total_blocks_found}\n`;
      reportContent += `- Cambios realizados: ${changeSummary.total_mappings_created}\n`;
      if (changeSummary.more_changes) {
        reportContent += `- Cambios adicionales no mostrados: +${changeSummary.more_changes}\n`;
      }
      reportContent += `\n`;
      
      if (changeSummary.changes && changeSummary.changes.length > 0) {
        reportContent += `CAMBIOS DETALLADOS:\n`;
        reportContent += `===========================================\n\n`;
        
        changeSummary.changes.forEach((change, index) => {
          reportContent += `${index + 1}. CAMBIO:\n`;
          reportContent += `   ----------------------------------------\n`;
          reportContent += `   ORIGINAL:\n`;
          reportContent += `   ${change.original}\n\n`;
          reportContent += `   MEJORADO:\n`;
          reportContent += `   ${change.improved}\n`;
          reportContent += `   ----------------------------------------\n\n`;
        });
        
        if (changeSummary.more_changes) {
          reportContent += `\n... y ${changeSummary.more_changes} cambios más realizados.\n`;
        }
      } else {
        reportContent += `No se realizaron cambios en el documento.\n`;
      }
      
      reportContent += `\n===========================================\n`;
      reportContent += `Fin del reporte\n`;
      reportContent += `===========================================\n`;

      // Create blob and download
      const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `change_report_${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('📋 Reporte de cambios descargado!');
    } catch (error) {
      console.error('Error downloading change report:', error);
      toast.error('Error al descargar el reporte');
    }
  };

  /**
   * Download improved CV
   */
  const handleDownloadPDF = async () => {
    if (!improvedPDF) return;
    if (!(await requireAuth())) {
      return;
    }
    
    try {
      if (improvedPDF.blob) {
        // Real PDF/DOCX blob from API
        const url = window.URL.createObjectURL(improvedPDF.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = improvedPDF.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('CV mejorado descargado correctamente');
      } else {
        // If no blob, try to regenerate from backend
        toast.info('Regenerando PDF...');
        
        try {
          const fileExtension = file.name.split('.').pop().toLowerCase();
          const formData = new FormData();
          formData.append('analysis_data', JSON.stringify(analysis));
          formData.append('output_format', fileExtension);

          const response = await axios.post('/api/cvs/generate-improved-cv/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            responseType: 'blob'
          });

          // Create blob from response
          const blob = new Blob([response.data], { 
            type: fileExtension === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          });
          
          // Download the blob
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = analysis
            ? filenameFromAnalysisData(analysis, fileExtension)
            : buildCvDownloadFilename({
                name: file?.name?.replace(/\.(pdf|docx)$/i, ''),
                extension: fileExtension,
              });
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          toast.success('CV mejorado descargado correctamente');
          
        } catch (apiError) {
          console.error('API regeneration failed:', apiError);
          toast.error('Error al descargar el CV. Inténtalo de nuevo.');
        }
      }
      
    } catch (error) {
      console.error('Error downloading CV:', error);
      toast.error('Error al descargar el CV. Inténtalo de nuevo.');
    }
  };

  /**
   * Reset the process
   */
  const handleReset = () => {
    setFile(null);
    setAnalysis(null);
    setImprovedPDF(null);
    setStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem 0' }}>
        {/* Header */}
        <div style={{ marginBottom: '3rem' }}>
          <button onClick={() => navigate('/dashboard/fast-results')} className="btn btn-secondary" style={{ marginBottom: '1rem' }}>
            ← Volver a resultados rápidos
          </button>
          <h1 style={{ color: '#333', marginBottom: '0.5rem' }}>📄 Mejorar PDF</h1>
          <p style={{ color: '#666' }}>Sube tu CV y obtén mejoras con IA</p>
        </div>

        {/* Progress Steps */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '3rem',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: step >= 1 ? '#3b82f6' : '#e5e7eb',
              color: step >= 1 ? '#fff' : '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              1
            </div>
            <span style={{ color: step >= 1 ? '#3b82f6' : '#6b7280', fontWeight: '500' }}>Subir CV</span>
            
            <div style={{ width: '50px', height: '2px', backgroundColor: step >= 2 ? '#3b82f6' : '#e5e7eb' }}></div>
            
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: step >= 2 ? '#3b82f6' : '#e5e7eb',
              color: step >= 2 ? '#fff' : '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              2
            </div>
            <span style={{ color: step >= 2 ? '#3b82f6' : '#6b7280', fontWeight: '500' }}>Análisis</span>
            
            <div style={{ width: '50px', height: '2px', backgroundColor: step >= 3 ? '#3b82f6' : '#e5e7eb' }}></div>
            
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: step >= 3 ? '#3b82f6' : '#e5e7eb',
              color: step >= 3 ? '#fff' : '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              3
            </div>
            <span style={{ color: step >= 3 ? '#3b82f6' : '#6b7280', fontWeight: '500' }}>Resultados</span>
          </div>
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div style={{
            backgroundColor: '#fff',
            padding: '3rem',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            textAlign: 'center',
            animation: 'fadeInUp 0.6s ease-out'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
              fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'
            }}>
              📄
            </div>
            
            <h2 style={{ marginBottom: '1rem', color: '#1f2937' }}>Sube tu CV</h2>
            <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '1.1rem' }}>
              Selecciona tu CV en PDF o Word (.docx). Lo analizaremos y te daremos mejoras.
            </p>

            <div style={{
              border: '2px dashed #d1d5db',
              borderRadius: '12px',
              padding: '2rem',
              marginBottom: '2rem',
              backgroundColor: '#f9fafb',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => fileInputRef.current?.click()}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#3b82f6';
              e.target.style.backgroundColor = '#eff6ff';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.backgroundColor = '#f9fafb';
            }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              
              {file ? (
                <div>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>✅</div>
                  <div style={{ fontWeight: '600', color: '#059669', marginBottom: '0.5rem' }}>
                    {file.name}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📁</div>
                  <div style={{ fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Haz clic para seleccionar el CV (PDF o Word)
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                    Tamaño máximo: 10 MB
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem', textAlign: 'left' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.9rem', color: '#4b5563' }}>
                <input
                  type="checkbox"
                  checked={aiConsent}
                  onChange={(e) => setAiConsent(e.target.checked)}
                  style={{ marginTop: '0.15rem' }}
                />
                <span>
                  Entiendo y acepto que el contenido de mi CV se envía a OpenAI solo para generar el análisis o la mejora. No se usa para entrenar modelos de IA ni se vende.
                </span>
              </label>
            </div>

            <button
              onClick={handleUploadAndAnalyze}
              disabled={!file || uploading || !aiConsent}
              style={{
                padding: '1rem 2rem',
                backgroundColor: file && !uploading && aiConsent ? '#3b82f6' : '#9ca3af',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: file && !uploading && aiConsent ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease'
              }}
            >
              {uploading ? '⏳ Subiendo...' : '🚀 Analizar y mejorar'}
            </button>
          </div>
        )}

        {/* Step 2: Analysis */}
        {step === 2 && (
          <div style={{
            backgroundColor: '#fff',
            padding: '2.5rem',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            animation: 'fadeInUp 0.6s ease-out'
          }}>
            <AnalyzingProgress
              active
              title={t('flow.analyzeProgressTitle')}
              steps={[
                t('flow.analyzeStep1'),
                t('flow.analyzeStep2'),
                t('flow.analyzeStep3'),
                t('flow.analyzeStep4'),
              ]}
            />

            <button
              type="button"
              onClick={() => setStep(1)}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Cancelar análisis
            </button>
          </div>
        )}

        {/* Template Gallery Modal */}
        {showTemplateGallery && analysis && (
          <TemplateGallery
            analysisData={analysis}
            onTemplateSelect={(template) => {
              setShowTemplateGallery(false);
              toast.success(`Plantilla ${template.name} aplicada correctamente`);
            }}
            onClose={() => setShowTemplateGallery(false)}
          />
        )}

        {/* Step 3: Results */}
        {step === 3 && analysis && (
          <div style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            {/* Score Improvement */}
            <div style={{
              backgroundColor: '#fff',
              padding: '2rem',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 style={{ margin: 0, color: '#1f2937' }}>¡Análisis completado!</h2>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => navigate('/dashboard/edit-cv', { state: { analysisData: analysis } })}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#047857';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#059669';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    ✏️ Editar secciones
                  </button>
                  <button
                    onClick={() => setShowTemplateGallery(true)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#1e3a8a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#1e40af';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#1e3a8a';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    🎨 Elegir plantilla
                  </button>
                </div>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '2rem',
                marginBottom: '2rem'
              }}>
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#fef3c7',
                  borderRadius: '12px',
                  border: '2px solid #fbbf24'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
                    {analysis.original_score}%
                  </div>
                  <div style={{ color: '#92400e', fontSize: '0.9rem' }}>Puntuación original</div>
                </div>
                
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#d1fae5',
                  borderRadius: '12px',
                  border: '2px solid #10b981'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🚀</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#065f46' }}>
                    {analysis.improved_score}%
                  </div>
                  <div style={{ color: '#065f46', fontSize: '0.9rem' }}>Puntuación mejorada</div>
                </div>
                
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#dbeafe',
                  borderRadius: '12px',
                  border: '2px solid #3b82f6'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📈</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>
                    +{analysis.improved_score - analysis.original_score}%
                  </div>
                  <div style={{ color: '#1e40af', fontSize: '0.9rem' }}>Mejora</div>
                </div>
              </div>
            </div>

            {/* NOTE: Original PDF Styles section REMOVED - Always use professional predefined styles */}

            {/* Detailed Analysis */}
            <div style={{
              backgroundColor: '#fff',
              padding: '2rem',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              marginBottom: '2rem'
            }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>Análisis detallado</h3>
              
              {analysis.improvements && Array.isArray(analysis.improvements) && analysis.improvements.map((improvement, index) => (
                <div key={index} style={{
                  padding: '1.5rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <h4 style={{ color: '#1f2937', fontWeight: '600', margin: 0 }}>
                      {improvement.section}
                    </h4>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      backgroundColor: improvement.impact === 'High' ? '#fef2f2' : 
                                     improvement.impact === 'Medium' ? '#fef3c7' : '#f0f9ff',
                      color: improvement.impact === 'High' ? '#dc2626' : 
                             improvement.impact === 'Medium' ? '#d97706' : '#2563eb'
                    }}>
                      Impacto: {improvement.impact}
                    </span>
                  </div>
                  <p style={{ color: '#6b7280', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    <strong>Problema:</strong> {improvement.issue}
                  </p>
                  <p style={{ color: '#374151', fontSize: '0.9rem' }}>
                    <strong>Sugerencia:</strong> {improvement.suggestion}
                  </p>
                </div>
              ))}
            </div>

            {/* ATS Compatibility */}
            <div style={{
              backgroundColor: '#fff',
              padding: '2rem',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              marginBottom: '2rem'
            }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>Compatibilidad ATS</h3>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: (analysis.ats_compatibility?.score || 0) >= 75
                    ? '#10b981'
                    : (analysis.ats_compatibility?.score || 0) >= 50
                      ? '#f59e0b'
                      : '#dc3545'
                }}>
                  {analysis.ats_compatibility?.score || 0}%
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#1f2937' }}>Puntuación ATS</div>
                  <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                    {(analysis.ats_compatibility?.score || 0) >= 75
                      ? 'Alta compatibilidad'
                      : (analysis.ats_compatibility?.score || 0) >= 50
                        ? 'Mejorable'
                        : (analysis.ats_compatibility?.score || 0) >= 40
                          ? 'Baja compatibilidad'
                          : 'Muy baja compatibilidad'}
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                  Áreas a mejorar:
                </div>
                {analysis.ats_compatibility && analysis.ats_compatibility.issues && Array.isArray(analysis.ats_compatibility.issues) && analysis.ats_compatibility.issues.map((issue, index) => (
                  <div key={index} style={{
                    color: '#6b7280',
                    fontSize: '0.9rem',
                    marginBottom: '0.25rem'
                  }}>
                    • {issue}
                  </div>
                ))}
              </div>
            </div>

            {/* Generate Improved PDF */}
            <div style={{
              backgroundColor: '#fff',
              padding: '2rem',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              textAlign: 'center'
            }}>
              <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>Generar CV mejorado</h3>
              <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                ¿Listo para crear tu CV mejorado? Elige el diseño y aplicaremos todas las mejoras sugeridas.
              </p>
              
              {/* PDF Layout Options - Always visible */}
              <div style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                backgroundColor: improvedPDF ? '#fef3c7' : '#f9fafb',
                borderRadius: '12px',
                border: improvedPDF ? '2px solid #f59e0b' : '2px solid #e5e7eb'
              }}>
                {/* Show layout options only for PDF files, not for Word documents */}
                {file && file.name.toLowerCase().endsWith('.pdf') ? (
                  <>
                    <h4 style={{ marginBottom: '1rem', color: '#374151', fontSize: '1rem' }}>
                      📄 Opciones de diseño {improvedPDF && <span style={{ fontSize: '0.85rem', color: '#92400e' }}>(Cambia y regenera)</span>}
                    </h4>
                    
                    {/* Page Limit Selection */}
                    <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#4b5563', fontWeight: '500' }}>
                        Límite de páginas:
                      </label>
                      <select
                        value={pageLimit}
                        onChange={(e) => setPageLimit(e.target.value)}
                        disabled={analyzing}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.95rem',
                          cursor: analyzing ? 'not-allowed' : 'pointer',
                          opacity: analyzing ? 0.6 : 1
                        }}
                      >
                        <option value="1_page">📃 1 página — Compacto (todo en una página)</option>
                        <option value="2_pages">📄 2 páginas — Estándar (profesional y equilibrado)</option>
                        <option value="auto">📋 Automático — Detallado (sin límites, amplio y legible)</option>
                      </select>
                    </div>
                    
                    {/* Layout Style Selection */}
                    <div style={{ textAlign: 'left' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#4b5563', fontWeight: '500' }}>
                        Estilo de diseño:
                      </label>
                      <select
                        value={layoutStyle}
                        onChange={(e) => setLayoutStyle(e.target.value)}
                        disabled={analyzing}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.95rem',
                          cursor: analyzing ? 'not-allowed' : 'pointer',
                          opacity: analyzing ? 0.6 : 1
                        }}
                      >
                        <option value="compact">🔄 Compacto — Fuentes pequeñas, espaciado ajustado</option>
                        <option value="standard">⭐ Estándar — Equilibrado y profesional (recomendado)</option>
                        <option value="detailed">✨ Detallado — Fuentes grandes, diseño amplio</option>
                      </select>
                    </div>
                  </>
                ) : (
                  // For Word documents, show a simple message
                  <>
                    <h4 style={{ marginBottom: '1rem', color: '#374151', fontSize: '1rem' }}>
                      🎨 Conservación del formato
                    </h4>
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '8px',
                      borderLeft: '4px solid #10b981'
                    }}>
                      <p style={{ margin: 0, color: '#065f46', fontSize: '0.9rem', lineHeight: '1.6' }}>
                        <strong>✨ Modo inteligente:</strong> Se conservará el formato original de tu documento Word.<br/>
                        Solo se mejorará el texto: tipografías, colores, diseño y estilos se mantienen iguales.
                      </p>
                    </div>
                  </>
                )}

                {/* NOTE: Original Style Toggle REMOVED - Always use professional predefined styles */}
                
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor: improvedPDF ? '#fef3c7' : '#dbeafe',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  color: improvedPDF ? '#92400e' : '#1e40af',
                  textAlign: 'left'
                }}>
                  {improvedPDF ? (
                    <>💡 <strong>¡Prueba distintos diseños!</strong> Cambia las opciones de arriba y pulsa «Regenerar» para ver el resultado.</>
                  ) : (
                    <>💡 <strong>Consejo:</strong> Para la mayoría de CVs profesionales, recomendamos «2 páginas — Estándar».</>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                {/* Generate/Regenerate Button */}
                <button
                  onClick={handleGenerateImprovedPDF}
                  disabled={analyzing}
                  style={{
                    padding: '1rem 2rem',
                    backgroundColor: analyzing ? '#9ca3af' : improvedPDF ? '#f59e0b' : '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: analyzing ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    flex: '1',
                    minWidth: '200px',
                    position: 'relative',
                    opacity: analyzing ? 0.7 : 1
                  }}
                >
                  {analyzing ? (
                    <span>
                      <span style={{ 
                        display: 'inline-block', 
                        animation: 'spin 1s linear infinite',
                        marginRight: '8px'
                      }}>⏳</span>
                      {improvedPDF ? 'Regenerando...' : 'Generando...'}
                    </span>
                  ) : improvedPDF ? (
                    '🔄 Regenerar con nuevo diseño'
                  ) : (
                    '✨ Generar CV mejorado'
                  )}
                </button>
                
                {/* Download Button - Only show if PDF exists and not regenerating */}
                {improvedPDF && !analyzing && (
                  <button
                    onClick={handleDownloadPDF}
                    style={{
                      padding: '1rem 2rem',
                      backgroundColor: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flex: '1',
                      minWidth: '200px'
                    }}
                  >
                    📥 Descargar CV actual
                  </button>
                )}
                
                {/* Download Change Report Button - Only show if changeSummary exists */}
                {changeSummary && improvedPDF && !analyzing && (
                  <button
                    onClick={handleDownloadChangeReport}
                    style={{
                      padding: '1rem 2rem',
                      backgroundColor: '#f59e0b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      flex: '1',
                      minWidth: '200px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#d97706';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#f59e0b';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    📋 Descargar Reporte de Cambios
                  </button>
                )}
              </div>
              
              {/* Change Summary Box - Show when overlay mode was used */}
              {changeSummary && improvedPDF && !analyzing && (
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  backgroundColor: '#fef3c7',
                  borderRadius: '12px',
                  border: '2px solid #f59e0b',
                  boxShadow: '0 4px 16px rgba(245, 158, 11, 0.15)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginBottom: '1rem',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#92400e'
                  }}>
                    <span>🔍</span>
                    <span>Resumen de cambios (modo superposición)</span>
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: '#fffbeb',
                    borderRadius: '8px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#78350f', marginBottom: '4px' }}>
                        Bloques Analizados
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#92400e' }}>
                        {changeSummary.total_blocks_found}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.8rem', color: '#78350f', marginBottom: '4px' }}>
                        Cambios Realizados
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669' }}>
                        {changeSummary.total_mappings_created}
                      </div>
                    </div>
                    {changeSummary.more_changes && (
                      <div>
                        <div style={{ fontSize: '0.8rem', color: '#78350f', marginBottom: '4px' }}>
                          Cambios Adicionales
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2563eb' }}>
                          +{changeSummary.more_changes}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {changeSummary.changes && changeSummary.changes.length > 0 && (
                    <div>
                      <div style={{ 
                        fontWeight: '600', 
                        marginBottom: '0.75rem',
                        color: '#92400e',
                        fontSize: '0.95rem'
                      }}>
                        Ejemplos de Cambios:
                      </div>
                      <div style={{ 
                        maxHeight: '300px', 
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        {changeSummary.changes.map((change, index) => (
                          <div key={index} style={{
                            padding: '0.75rem',
                            backgroundColor: '#fff',
                            borderRadius: '6px',
                            border: '1px solid #fbbf24',
                            fontSize: '0.85rem'
                          }}>
                            <div style={{ marginBottom: '6px' }}>
                              <span style={{ 
                                fontWeight: '600', 
                                color: '#dc2626',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase'
                              }}>
                                Original:
                              </span>
                              <div style={{ 
                                color: '#6b7280',
                                marginTop: '4px',
                                fontStyle: 'italic',
                                padding: '6px',
                                backgroundColor: '#f3f4f6',
                                borderRadius: '4px'
                              }}>
                                {change.original}
                              </div>
                            </div>
                            <div style={{ marginTop: '6px' }}>
                              <span style={{ 
                                fontWeight: '600', 
                                color: '#059669',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase'
                              }}>
                                Mejorado:
                              </span>
                              <div style={{ 
                                color: '#374151',
                                marginTop: '4px',
                                fontWeight: '500',
                                padding: '6px',
                                backgroundColor: '#ecfdf5',
                                borderRadius: '4px'
                              }}>
                                {change.improved}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {changeSummary.total_mappings_created === 0 && (
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#fef2f2',
                      borderRadius: '8px',
                      border: '1px solid #fca5a5',
                      color: '#991b1b',
                      textAlign: 'center'
                    }}>
                      ⚠️ No se realizaron cambios. El PDF conserva el contenido original.
                    </div>
                  )}
                </div>
              )}
              
              <AnalyzingProgress
                active={analyzing}
                title={t('flow.generateProgressTitle')}
                steps={[
                  t('flow.generateStep1'),
                  t('flow.generateStep2'),
                  t('flow.generateStep3'),
                ]}
              />
              
              {/* Success Message - Only show after generation */}
              {improvedPDF && (
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  backgroundColor: '#d1fae5',
                  borderRadius: '8px',
                  border: '1px solid #10b981'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                  <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '0.5rem' }}>
                    ¡CV mejorado generado correctamente!
                  </div>
                  <div style={{ color: '#065f46', fontSize: '0.9rem' }}>
                    {improvedPDF.improvements_applied} mejoras aplicadas • Formato: {improvedPDF.originalFormat.toUpperCase()}
                  </div>
                  <div style={{ color: '#047857', fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                    Diseño: {pageLimit === '1_page' ? '1 página' : pageLimit === '2_pages' ? '2 páginas' : 'Automático'} • 
                    Estilo: {layoutStyle === 'compact' ? 'Compacto' : layoutStyle === 'detailed' ? 'Detallado' : 'Estándar'}
                  </div>
                </div>
              )}
              
              {/* Reset Button - Always available */}
              {improvedPDF && (
                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <button
                    onClick={handleReset}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'transparent',
                      color: '#6b7280',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: '500'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#f3f4f6';
                      e.target.style.borderColor = '#9ca3af';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#d1d5db';
                    }}
                  >
                    🔄 Empezar de nuevo - Analizar otro PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {upgradePrompt}
    </>
  );
};

export default PDFImprovement;
